import Box from "@mui/material/Box";
import ThemeContext from "components/app/Context";
import BookmarkPlan from "components/common/Bookmark";
import Export from "components/common/Export";
import { exportURLs } from "components/common/Export/ExportAPIEndpoints";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { fetchBookmarkedInncodeAsync } from "components/features/dashboard/Dashboard.slice";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { abovePropertyViewTypeProperty } from "utils/CommonConstants";
import { copyCreatedDateFilterPayload, formFilterRequestPayload, getHltInncodes } from "utils/CommonFunctions";
import { serviceTypes } from "utils/Types";
import {
  fetchAboveProperties,
  setAbovePropertiesData,
  setAllProperties,
  setHotelViewSnapshot,
  setLoader
} from "../AboveProperty.slice";
import { getKeyMetricsHotelViewData } from "../AbovePropertyHelper";
import BookmarkedProperties from "../BookmarkedProperties";
import { abovePropertyHeader, getColor } from "./TableHeadersConfig";

const PropertyView = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const [page, setPage] = useState<any>({ pageNo: 0 });
  const [totalPage, setTotalPage] = useState<any>(0);
  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const { isMobileScreen } = useContext(ThemeContext);
  const pageName = filterPage.hotelDisplay;

  const { aboveProperties, bookmarkedProperties, hotelViewSnapshot } = useAppSelector((state) => state.aboveProperty);
  const navigate = useNavigate();
  const innCodes = localStorage.getItem("hltInnCodes");
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[pageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);
  const searchText = globalFiltersforPage.searchText;

  const getModifiedRes = (res: any) => {
    const response = res?.payload?.properties?.map((el: any) => {
      if (!el?.propertyName) {
        el = { ...el, propertyName: el?.innCode };
      }
      return el;
    });
    return response;
  };

  const getPayload = () => {
    return {
      searchText,
      ...formattedFilterRequestPayload,
      innCodes: getHltInncodes(),
      pageable: {
        page: page.pageNo,
        size: 50
      }
    };
  };

  const fetchHotelViewSnapshot = () => {
    dispatch(
      fetchAboveProperties({
        searchText,
        ...formattedFilterRequestPayload,
        innCodes: getHltInncodes(),
        fetchSnapshot: true
      })
    ).then((res: any) => {
      dispatch(setHotelViewSnapshot(res?.payload));
    });
  };

  useEffect(() => {
    if (initialLoad) {
      dispatch(fetchAboveProperties(getPayload())).then((res: any) => {
        const totalPage = res.payload?.meta?.page?.total;
        if (page.pageNo === 0) {
          const filteredData = getModifiedRes(res);
          dispatch(setAbovePropertiesData(filteredData));
        } else {
          const filteredData = getModifiedRes(res);
          const above = [...aboveProperties, ...filteredData];
          dispatch(setAbovePropertiesData(above));
        }
        setTotalPage(totalPage);
        dispatch(setAllProperties(res?.payload?.properties));

        dispatch(fetchBookmarkedInncodeAsync({}));
      });
    }
  }, [page, initialLoad]);

  useEffect(() => {
    dispatch(setLoader(true));
    setPage((prevPage: any) => ({ ...prevPage, pageNo: 0 }));
    setInitialLoad(true);
  }, [filterRequestPayload, searchText, innCodes]);

  useEffect(() => {
    fetchHotelViewSnapshot();
  }, [filterRequestPayload, innCodes]);

  const setPageNo = (page: any) => {
    setPage((prevPage: any) => ({ ...prevPage, pageNo: page }));
  };

  const rows = aboveProperties?.length ? [...aboveProperties?.map((a: any) => ({ ...a, id: a?.innCode }))] : [];

  return (
    <>
      {isMobileScreen && <BookmarkedProperties />}
      <Box
        className="properties_view"
        sx={{
          backgroundColor: tokens.Background.General.Primary,
          borderRadius: "12px",
          overflow: "hidden",
          mt: isMobileScreen ? "32px" : 0,
          mb: "32px"
        }}
      >
        <Box sx={{ padding: "16px 8px 0 8px" }}>
          <GlobalFilters
            isSearchBarVisible
            searchBarPlaceholder="Search by Property"
            page={pageName}
            isPreDefinedNav={true}
            searchedText={searchText}
          />
          <GlobalFilterDescription page={pageName} />
          <div className="key-metrics-wrapper">
            <KeyMetrics keyMetricsData={getKeyMetricsHotelViewData(hotelViewSnapshot)} helperText="*Last 90 Days" />
          </div>
          <div className="export">
            <Export
              URL={exportURLs.aboveProperty_propertiesPaged}
              data={getPayload()}
              serviceType={serviceTypes.userplan}
            />
          </div>
        </Box>
        <div>
          <BasicTable
            totalRecords={totalPage}
            pagination={{ pageNo: page.pageNo, setPageNo }}
            noDataMessage={translate("common.noResults")}
            data={{
              headers: abovePropertyHeader,
              rows: rows.map((row: any) => {
                const isBookmarked = bookmarkedProperties?.some((b: any) => b?.innCode === row?.id);
                return {
                  data: [
                    {
                      content: (
                        <Box
                          onClick={() => {
                            copyCreatedDateFilterPayload(
                              filterRequestPayload,
                              dispatch,
                              filterPage.individualPropertyPlansDisplay
                            );
                            navigate(`/property/${row?.id}?parentTab=Property+Plans`);
                          }}
                          sx={{
                            textDecoration: "none",
                            cursor: "pointer",
                            color: tokens.Text.General.PrimaryHeadline,
                            fontWeight: "700"
                          }}
                        >
                          {row.propertyName}
                        </Box>
                      ),
                      showBold: true
                    },
                    {
                      content: row?.innCode || "-"
                    },
                    { content: row?.brandName || "-" },
                    {
                      content: row?.leaderPlans || "-"
                    },
                    {
                      content: row?.teamPlans || "-"
                    },
                    {
                      content: (
                        <Typography variant="body2" color={getColor(row?.engagement)}>
                          {row?.engagement || "-"}
                        </Typography>
                      )
                    },
                    {
                      content: (
                        <BookmarkPlan
                          aboveUserPayload={{
                            isAboveUsertype: true,
                            viewType: abovePropertyViewTypeProperty,
                            inncode: row?.id
                          }}
                          isBookmarked={isBookmarked}
                        />
                      )
                    }
                  ]
                };
              })
            }}
          />
        </div>
      </Box>
    </>
  );
};

export default PropertyView;
